import { useState } from 'react';

import { AddIcon } from './icons';

/**
 * Small-form "add a row" pattern (card 059).
 *
 * Use when the create is trivial enough that a full wizard is
 * overkill — eg "add a site origin", "add a tag".  Keeps every
 * inline add on the dashboard visually identical:
 *
 *   ┌─────────────────────────────────────────────┬────────┐
 *   │ LABEL                                       │        │
 *   │ [────────────── input ──────────────]       │ [ Add ] │
 *   └─────────────────────────────────────────────┴────────┘
 *
 * Alignment rules — all enforced via the grid, not ad-hoc styling:
 *   - Input + button share the same height (32px button, 34px input
 *     after `1px border each side`; the `align-items: end` on the
 *     grid lines the bottoms up with the label).
 *   - Labels are above inputs, never inside, never to the left.  Mix-
 *     ing label positions in the same form is the alignment anti-
 *     pattern the user called out.
 *   - The CTA button never floats centred between label + input —
 *     it sits on the input baseline (`align-items: end`).
 *
 * API surface:
 *
 *   <InlineAddForm
 *     label="Origin"
 *     placeholder="https://app.acme.com"
 *     buttonLabel="Add origin"
 *     icon={<GlobeIcon />}              // optional, default <AddIcon />
 *     hint="HTTPS only. Wildcards aren't supported."  // optional
 *     validate={(v) => v.startsWith('https://')}      // optional
 *     onSubmit={async (value) => { … }}
 *   />
 *
 * The component owns the value, busy, and error state so the parent
 * just passes an async submitter.  On a successful submit the input
 * clears.  Errors surface inline under the input.
 */
export function InlineAddForm({
  label,
  placeholder,
  buttonLabel,
  icon,
  hint,
  type = 'text',
  validate,
  onSubmit,
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const trimmed = value.trim();
  const validatorOk = validate ? validate(trimmed) : true;
  const canSubmit = !busy && trimmed.length > 0 && validatorOk;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(trimmed);
      setValue('');
    } catch (ex) {
      setErr(ex?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="inline-add" onSubmit={submit}>
      <label className="inline-add__field">
        <span className="inline-add__label">{label}</span>
        <input
          className="input inline-add__input"
          type={type}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
        />
      </label>
      <button
        type="submit"
        className="btn btn--primary inline-add__btn"
        disabled={!canSubmit}
      >
        {icon || <AddIcon />}
        {busy ? 'Adding…' : (buttonLabel || 'Add')}
      </button>
      {hint && <p className="inline-add__hint">{hint}</p>}
      {err && <p className="inline-add__err">{err}</p>}
      <style>{INLINE_ADD_CSS}</style>
    </form>
  );
}

const INLINE_ADD_CSS = `
  .inline-add {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 10px;
    /* The hint + error wrap to a third row that spans both columns
       so they sit under the input rather than next to the button. */
    grid-template-rows: auto auto;
    align-items: end;
  }
  .inline-add__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .inline-add__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-2, #334155);
  }
  .inline-add__input {
    width: 100%;
  }
  /* The .btn class already sets a 32-34px height; align-items: end on
     the grid lines its bottom up with the input bottom. */
  .inline-add__btn {
    grid-column: 2;
    align-self: end;
    white-space: nowrap;
  }
  .inline-add__hint,
  .inline-add__err {
    grid-column: 1 / -1;
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
  }
  .inline-add__hint { color: var(--text-3, #64748b); }
  .inline-add__err { color: var(--danger-700, #b91c1c); }
  @media (max-width: 520px) {
    .inline-add {
      grid-template-columns: 1fr;
    }
    .inline-add__btn {
      grid-column: 1;
      width: 100%;
      justify-content: center;
    }
  }
`;
